import { isPaymentAccount } from '@outss/ledger-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  ChevronDown,
  Download,
  Lock,
  Printer,
  Trash2,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { AmountInput } from '@/components/amount-input';
import { Combobox } from '@/components/combobox';
import { DateInput } from '@/components/date-input';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { VatStatusBadge } from '@/components/vat';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { ltr } from '@/lib/arabic';
import { formatTrn, useMe } from '@/lib/clients';
import { displayDate, todayIso } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useLanguage, useT } from '@/lib/i18n';
import { sourceLabel } from '@/lib/journal';
import { useOrgId } from '@/lib/org';
import {
  ADJUSTABLE_BOXES,
  boxSection,
  downloadCsv,
  negate,
  useVatExceptions,
  useVatReturn,
  vatReturnCsv,
  type VatBox,
  type VatReturn,
} from '@/lib/vat';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/taxes/vat/returns/$key')({ component: VatReturnPage });

const SECTIONS = [
  { key: 'sales', title: 'taxPages.vatReturn.sectionSales' },
  { key: 'expenses', title: 'taxPages.vatReturn.sectionExpenses' },
  { key: 'net', title: 'taxPages.vatReturn.sectionNet' },
] as const satisfies ReadonlyArray<{ key: ReturnType<typeof boxSection>; title: string }>;

/** VAT 201 boxes → the FTA's Arabic wording; English keeps the label the server sends. */
const BOX_KEYS = {
  '1a': 'taxPages.vatBoxes.b1a',
  '1b': 'taxPages.vatBoxes.b1b',
  '1c': 'taxPages.vatBoxes.b1c',
  '1d': 'taxPages.vatBoxes.b1d',
  '1e': 'taxPages.vatBoxes.b1e',
  '1f': 'taxPages.vatBoxes.b1f',
  '1g': 'taxPages.vatBoxes.b1g',
  '2': 'taxPages.vatBoxes.b2',
  '3': 'taxPages.vatBoxes.b3',
  '4': 'taxPages.vatBoxes.b4',
  '5': 'taxPages.vatBoxes.b5',
  '6': 'taxPages.vatBoxes.b6',
  '7': 'taxPages.vatBoxes.b7',
  '8': 'taxPages.vatBoxes.b8',
  '9': 'taxPages.vatBoxes.b9',
  '10': 'taxPages.vatBoxes.b10',
  '11': 'taxPages.vatBoxes.b11',
  '12': 'taxPages.vatBoxes.b12',
  '13': 'taxPages.vatBoxes.b13',
  '14': 'taxPages.vatBoxes.b14',
} as const;

/** A box's label in the reader's language. */
function useBoxLabel(): (box: string, serverLabel: string) => string {
  const t = useT();
  const lang = useLanguage();
  return (box, serverLabel) =>
    lang === 'ar' && box in BOX_KEYS ? t(BOX_KEYS[box as keyof typeof BOX_KEYS]) : serverLabel;
}
const TOTALS = new Set(['8', '11', '12', '13', '14']);

/** Screen T2 (document 06): the VAT 201 working for one period. */
function VatReturnPage() {
  const t = useT();
  const { key } = Route.useParams();
  const orgId = useOrgId();
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const ret = useVatReturn(key);
  const exceptions = useVatExceptions(key);
  const [dialog, setDialog] = useState<null | 'file' | 'pay' | 'reopen' | 'adjust'>(null);
  const r = ret.data;

  if (!orgId)
    return <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />;
  if (ret.isPending) return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  if (ret.isError || !r)
    return <EmptyState title={t('taxPages.vatReturn.loadError')} hint={ret.error?.message} />;

  const filed = r.filing !== null;
  const refund = r.netVat.startsWith('-');
  const outstanding = outstandingOf(r);
  const canPay = staff && filed && outstanding !== '0.00';
  const canAdjust = staff && !filed;
  const derivedAdjusted = r.adjustments.length > 0;

  return (
    <>
      <Link
        to="/taxes/vat"
        className="mb-2 inline-flex items-center gap-1 text-sm text-primary hover:underline print:hidden"
      >
        <ArrowLeft className="rtl:-scale-x-100 size-4" /> {t('taxPages.vatReturn.backToVat')}
      </Link>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-3 font-display text-[1.7rem] font-medium tracking-[-0.01em]">
            {t('taxPages.vatReturn.heading', { label: r.label })} <VatStatusBadge r={r} />
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <bdi>{r.orgName}</bdi> ·{' '}
            {t('taxPages.vatShared.trnValue', { trn: ltr(formatTrn(r.trn) || '—') })} ·{' '}
            {t('taxPages.vatShared.periodRange', {
              from: displayDate(r.periodStart),
              to: displayDate(r.periodEnd),
            })}{' '}
            · {t('taxPages.vatShared.dueOn', { date: displayDate(r.dueDate) })}
          </p>
        </div>
        <div className="flex items-center gap-2 print:hidden">
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('taxPages.vatReturn.exportCsv')}
            title={t('taxPages.vatReturn.exportCsv')}
            onClick={() =>
              downloadCsv(t('taxPages.vatReturn.csvFileName', { label: r.label }), vatReturnCsv(r))
            }
          >
            <Download className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('taxPages.vatReturn.print')}
            onClick={() => window.print()}
          >
            <Printer className="size-4" />
          </Button>
          {canAdjust ? (
            <Button variant="outline" onClick={() => setDialog('adjust')}>
              {t('taxPages.vatReturn.addAdjustment')}
            </Button>
          ) : null}
          {r.canFile ? (
            <Button onClick={() => setDialog('file')}>{t('taxPages.vatReturn.markAsFiled')}</Button>
          ) : null}
          {canPay ? (
            <Button onClick={() => setDialog('pay')}>
              {refund
                ? t('taxPages.vatShared.recordRefund')
                : t('taxPages.vatShared.recordPayment')}
            </Button>
          ) : null}
          {r.canReopen ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" aria-label={t('taxPages.vatReturn.moreActions')}>
                  {t('taxPages.vatReturn.more')} <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setDialog('reopen')}>
                  {t('taxPages.vatReturn.reopenReturn')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        <Stat
          label={
            refund
              ? t('taxPages.vatShared.netVatRefundable')
              : t('taxPages.vatShared.netVatPayable')
          }
          value={`${r.currency} ${formatAmount(refund ? negate(r.netVat) : r.netVat)}`}
          strong
        />
        <Stat label={t('taxPages.vatReturn.vatOnSalesBox')} value={formatAmount(r.outputVat)} />
        <Stat label={t('taxPages.vatReturn.vatOnPurchasesBox')} value={formatAmount(r.inputVat)} />
        <Stat
          label={refund ? t('taxPages.vatReturn.refunded') : t('taxPages.vatReturn.paid')}
          value={filed ? formatAmount(r.settled) : '—'}
          hint={
            filed && outstanding !== '0.00'
              ? t('taxPages.vatReturn.outstandingHint', { amount: ltr(formatAmount(outstanding)) })
              : undefined
          }
        />
      </div>

      {r.filing ? (
        <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
          <Lock className="size-4 text-muted-foreground" aria-hidden />
          <span>
            {r.filing.filedBy ? (
              <Trans
                i18nKey="taxPages.vatReturn.filedOnBy"
                values={{
                  date: r.filedOn ? displayDate(r.filedOn) : '',
                  name: r.filing.filedBy,
                }}
                components={{ b: <bdi /> }}
              />
            ) : (
              t('taxPages.vatReturn.filedOn', { date: r.filedOn ? displayDate(r.filedOn) : '' })
            )}
            {r.filing.reference
              ? ` · ${t('taxPages.vatReturn.ftaReference', { reference: ltr(r.filing.reference) })}`
              : ''}
          </span>
          {r.filing.returnEntryId ? (
            <span>
              ·{' '}
              <Trans
                i18nKey="taxPages.vatReturn.returnJournal"
                values={{ no: r.filing.returnEntryNo }}
                components={{
                  lnk: (
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: r.filing.returnEntryId }}
                      className="text-primary hover:underline"
                    />
                  ),
                }}
              />
            </span>
          ) : null}
          <span className="text-muted-foreground">· {t('taxPages.vatReturn.lockedNote')}</span>
        </div>
      ) : r.status === 'open' ? (
        <div className="mb-4 rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
          {t('taxPages.vatReturn.runningNote', { date: displayDate(r.periodEnd) })}
        </div>
      ) : null}
      {r.filing?.changedSinceFiling ? (
        <div className="mb-4 rounded-md border border-[#B45309]/30 bg-[#B45309]/5 px-4 py-3 text-sm text-[#B45309]">
          {t('taxPages.vatReturn.changedSinceFiling')}
        </div>
      ) : null}
      {derivedAdjusted && !filed ? (
        <div className="mb-4 rounded-lg border bg-card px-4 py-3 text-sm text-muted-foreground">
          {t('taxPages.vatReturn.adjustmentsNote', {
            amount: ltr(`${r.currency} ${formatAmount(r.bookedNetVat)}`),
          })}
        </div>
      ) : null}

      <div className="mb-6 rounded-lg border bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="w-[56px] px-4 py-2 text-start font-medium">
                  {t('taxPages.vatReturn.colBox')}
                </th>
                <th className="py-2 text-start font-medium">
                  {t('taxPages.vatReturn.colDescription')}
                </th>
                <th className="w-[160px] py-2 text-end font-medium">
                  {t('taxPages.vatReturn.colAmountCurrency', { currency: r.currency })}
                </th>
                <th className="w-[160px] py-2 text-end font-medium">
                  {t('taxPages.vatReturn.colVatCurrency', { currency: r.currency })}
                </th>
                <th className="w-[150px] px-4 py-2 text-end font-medium">
                  {t('taxPages.vatReturn.colAdjustment')}
                </th>
              </tr>
            </thead>
            {SECTIONS.map((sec) => (
              <tbody key={sec.key}>
                <tr className="bg-muted/50">
                  <td colSpan={5} className="px-4 py-1.5 text-xs font-semibold">
                    {t(sec.title)}
                  </td>
                </tr>
                {r.boxes
                  .filter((b) => boxSection(b.box) === sec.key)
                  .map((b) => (
                    <BoxRow key={b.box} r={r} b={b} />
                  ))}
              </tbody>
            ))}
          </table>
        </div>
      </div>

      <Section title={t('taxPages.vatReturn.adjustmentsTitle', { count: r.adjustments.length })}>
        {r.adjustments.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {t('taxPages.vatReturn.noAdjustments')}
          </p>
        ) : (
          <AdjustmentTable r={r} canRemove={canAdjust} />
        )}
      </Section>

      <Section
        title={
          exceptions.data
            ? t('taxPages.vatReturn.exceptionsTitleCount', { count: exceptions.data.length })
            : t('taxPages.vatReturn.exceptionsTitle')
        }
        hint={t('taxPages.vatReturn.exceptionsHint')}
      >
        {exceptions.isPending ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {t('taxPages.vatReturn.checking')}
          </p>
        ) : exceptions.isError ? (
          <p className="px-4 py-3 text-sm text-destructive">{exceptions.error.message}</p>
        ) : exceptions.data.length === 0 ? (
          <p className="px-4 py-3 text-sm text-muted-foreground">
            {t('taxPages.vatReturn.noExceptions')}
          </p>
        ) : (
          <ul className="divide-y">
            {exceptions.data.map((x, i) => (
              <li key={i} className="flex gap-3 px-4 py-2.5 text-sm" data-kind={x.kind}>
                {x.severity === 'error' ? (
                  <AlertCircle
                    className="mt-0.5 size-4 shrink-0 text-[#B42318]"
                    aria-label={t('taxPages.vatReturn.error')}
                  />
                ) : (
                  <AlertTriangle
                    className="mt-0.5 size-4 shrink-0 text-[#B45309]"
                    aria-label={t('taxPages.vatReturn.warning')}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{x.title}</div>
                  <div className="text-muted-foreground">{x.message}</div>
                  {x.entryId ? (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {x.date ? displayDate(x.date) : ''} · {sourceLabel(x.sourceType ?? '')}{' '}
                      <Link
                        to="/accounting/journal-entries/$entryId"
                        params={{ entryId: x.entryId }}
                        className="text-primary hover:underline"
                      >
                        {t('taxPages.vatReturn.entryNo', { no: x.entryNo })}
                      </Link>
                      {x.contactName ? (
                        <>
                          {' · '}
                          <bdi>{x.contactName}</bdi>
                        </>
                      ) : null}
                      {x.memo ? (
                        <>
                          {' · '}
                          <bdi>{x.memo}</bdi>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
                {x.amount ? (
                  <div className="tnum shrink-0 text-end">{formatAmount(x.amount)}</div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Section>

      {filed ? (
        <Section
          title={refund ? t('taxPages.vatReturn.refunds') : t('taxPages.vatReturn.payments')}
        >
          {r.payments.length === 0 ? (
            <p className="px-4 py-3 text-sm text-muted-foreground">
              {outstanding === '0.00'
                ? t('taxPages.vatReturn.nothingToPay')
                : t('taxPages.vatReturn.nothingRecorded')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 text-start font-medium">
                      {t('taxPages.vatReturn.colDate')}
                    </th>
                    <th className="py-2 text-start font-medium">
                      {t('taxPages.vatReturn.colType')}
                    </th>
                    <th className="py-2 text-start font-medium">
                      {t('taxPages.vatReturn.colAccount')}
                    </th>
                    <th className="py-2 text-start font-medium">
                      {t('taxPages.vatReturn.colReference')}
                    </th>
                    <th className="py-2 text-start font-medium">
                      {t('taxPages.vatReturn.colJournal')}
                    </th>
                    <th className="px-4 py-2 text-end font-medium">
                      {t('taxPages.vatReturn.colAmount')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {r.payments.map((p) => (
                    <tr key={p.id} className="border-b last:border-0">
                      <td className="px-4 py-2">{displayDate(p.date)}</td>
                      <td className="py-2">
                        {p.kind === 'payment'
                          ? t('taxPages.vatReturn.paymentToFta')
                          : t('taxPages.vatReturn.refundFromFta')}
                      </td>
                      <td className="py-2">{p.bankAccountName}</td>
                      <td className="py-2">{p.reference ?? ''}</td>
                      <td className="py-2">
                        <Link
                          to="/accounting/journal-entries/$entryId"
                          params={{ entryId: p.entryId }}
                          className="text-primary hover:underline"
                        >
                          {t('taxPages.vatReturn.entryNo', { no: p.entryNo })}
                        </Link>
                      </td>
                      <td className="tnum px-4 py-2 text-end">{formatAmount(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      ) : null}

      <FileDialog
        open={dialog === 'file'}
        r={r}
        exceptionCount={exceptions.data?.length ?? 0}
        onClose={() => setDialog(null)}
      />
      <PaymentDialog
        open={dialog === 'pay'}
        r={r}
        outstanding={outstanding}
        onClose={() => setDialog(null)}
      />
      <ReopenDialog open={dialog === 'reopen'} r={r} onClose={() => setDialog(null)} />
      <AdjustmentDialog open={dialog === 'adjust'} r={r} onClose={() => setDialog(null)} />
    </>
  );
}

function outstandingOf(r: VatReturn): string {
  if (!r.filing) return '0.00';
  const cents = (s: string) => {
    const neg = s.startsWith('-');
    const [w, f = ''] = (neg ? s.slice(1) : s).split('.');
    const v = BigInt(w!) * 100n + BigInt(f.padEnd(2, '0').slice(0, 2));
    return neg ? -v : v;
  };
  const net = cents(r.netVat);
  const due = (net < 0n ? -net : net) - cents(r.settled);
  const v = due < 0n ? 0n : due;
  return `${v / 100n}.${String(v % 100n).padStart(2, '0')}`;
}

function Stat({
  label,
  value,
  hint,
  strong,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
  strong?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('tnum mt-1 text-end text-lg', strong && 'font-semibold')}>{value}</div>
      {hint ? <div className="text-end text-xs text-[#B45309]">{hint}</div> : null}
    </div>
  );
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="mb-6 rounded-lg border bg-card">
      <header className="border-b px-4 py-2.5">
        <h2 className="text-base font-semibold">{title}</h2>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </header>
      {children}
    </section>
  );
}

function BoxRow({ r, b }: { r: VatReturn; b: VatBox }) {
  const t = useT();
  const boxLabel = useBoxLabel();
  const total = TOTALS.has(b.box);
  const drill = (value: string | null) =>
    value === null ? null : (
      <Link
        to="/reports/vat-detail"
        search={{ from: r.periodStart, to: r.periodEnd, box: b.box }}
        className="text-primary hover:underline"
      >
        {formatAmount(value)}
      </Link>
    );
  const adj = [
    b.adjAmount && b.adjAmount !== '0.00'
      ? t('taxPages.vatReturn.adjAmount', { amount: ltr(formatAmount(b.adjAmount)) })
      : null,
    b.adjVat && b.adjVat !== '0.00'
      ? t('taxPages.vatReturn.adjVat', { amount: ltr(formatAmount(b.adjVat)) })
      : null,
  ].filter(Boolean);
  return (
    <tr
      className={cn('border-b border-dashed last:border-0', total && 'font-semibold')}
      data-box={b.box}
    >
      <td className="px-4 py-1.5">{b.box}</td>
      <td className="py-1.5">{boxLabel(b.box, b.label)}</td>
      <td className="tnum py-1.5 text-end">{drill(b.totalAmount)}</td>
      <td className="tnum py-1.5 text-end">{drill(b.totalVat)}</td>
      <td className="tnum px-4 py-1.5 text-end text-xs text-muted-foreground">{adj.join(' · ')}</td>
    </tr>
  );
}

function AdjustmentTable({ r, canRemove }: { r: VatReturn; canRemove: boolean }) {
  const t = useT();
  const orgId = useOrgId();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/adjustments/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vat', orgId] });
      toast(t('taxPages.vatReturn.adjustmentRemoved'));
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
            <th className="w-[56px] px-4 py-2 text-start font-medium">
              {t('taxPages.vatReturn.colBox')}
            </th>
            <th className="py-2 text-start font-medium">{t('taxPages.vatReturn.colReason')}</th>
            <th className="w-[160px] py-2 text-start font-medium">
              {t('taxPages.vatReturn.colBy')}
            </th>
            <th className="w-[130px] py-2 text-end font-medium">
              {t('taxPages.vatReturn.colAmount')}
            </th>
            <th className="w-[130px] py-2 text-end font-medium">
              {t('taxPages.vatReturn.colVat')}
            </th>
            <th className="w-[56px] px-4 py-2" />
          </tr>
        </thead>
        <tbody>
          {r.adjustments.map((a) => (
            <tr key={a.id} className="border-b last:border-0">
              <td className="px-4 py-2">{a.box}</td>
              <td className="py-2">{a.reason}</td>
              <td className="py-2 text-muted-foreground">
                {a.createdBy ?? ''} · {displayDate(a.createdAt.slice(0, 10))}
              </td>
              <td className="tnum py-2 text-end">{formatAmount(a.amount)}</td>
              <td className="tnum py-2 text-end">{formatAmount(a.vat)}</td>
              <td className="px-4 py-2 text-end">
                {canRemove ? (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t('taxPages.vatReturn.removeAdjustment', { box: ltr(a.box) })}
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function useInvalidate() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['vat', orgId] });
}

function FileDialog({
  open,
  r,
  exceptionCount,
  onClose,
}: {
  open: boolean;
  r: VatReturn;
  exceptionCount: number;
  onClose: () => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const qc = useQueryClient();
  const [filedOn, setFiledOn] = useState(todayIso());
  const [reference, setReference] = useState('');
  const [postJournal, setPostJournal] = useState(true);
  useEffect(() => {
    if (open) {
      setFiledOn(todayIso());
      setReference('');
      setPostJournal(true);
    }
  }, [open]);
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/file`, {
        method: 'POST',
        body: JSON.stringify({ filedOn, reference: reference.trim(), postJournal }),
      }),
    onSuccess: async (saved) => {
      await invalidate();
      await qc.invalidateQueries({ queryKey: ['journal-entries'] });
      toast(t('taxPages.vatReturn.markedFiled', { label: ltr(saved.label) }), {
        ...(saved.filing?.returnEntryId
          ? {
              link: {
                to: `/accounting/journal-entries/${saved.filing.returnEntryId}`,
                label: t('taxPages.vatReturn.viewJournal'),
              },
            }
          : {}),
      });
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('taxPages.vatReturn.fileTitle', { label: r.label })}</DialogTitle>
          <DialogDescription>
            {t(
              r.netVat.startsWith('-')
                ? 'taxPages.vatReturn.fileDescriptionRefundable'
                : 'taxPages.vatReturn.fileDescriptionPayable',
              {
                from: displayDate(r.periodStart),
                to: displayDate(r.periodEnd),
                amount: ltr(
                  `${r.currency} ${formatAmount(r.netVat.startsWith('-') ? negate(r.netVat) : r.netVat)}`,
                ),
              },
            )}
          </DialogDescription>
        </DialogHeader>
        {exceptionCount > 0 ? (
          <div className="rounded-md border border-[#B45309]/30 bg-[#B45309]/5 px-3 py-2 text-sm text-[#B45309]">
            {t('taxPages.vatReturn.exceptionsStillListed', { count: exceptionCount })}
          </div>
        ) : null}
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3">
          <Field label={t('taxPages.vatReturn.dateFiled')} error={errors['filedOn']}>
            {(id) => <DateInput id={id} value={filedOn} onChange={setFiledOn} />}
          </Field>
          <Field
            label={t('taxPages.vatReturn.ftaReferenceOptional')}
            hint={t('taxPages.vatReturn.ftaReferenceHint')}
          >
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={60}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
          <Checkbox
            label={t('taxPages.vatReturn.postJournal')}
            checked={postJournal}
            onChange={(e) => setPostJournal(e.target.checked)}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            {m.isPending ? t('taxPages.vatReturn.filing') : t('taxPages.vatReturn.markAsFiled')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({
  open,
  r,
  outstanding,
  onClose,
}: {
  open: boolean;
  r: VatReturn;
  outstanding: string;
  onClose: () => void;
}) {
  const t = useT();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const accounts = useAccounts();
  const refund = r.netVat.startsWith('-');
  const banks = (accounts.data ?? []).filter(
    (a) => a.isActive && !a.isControl && isPaymentAccount(a.detailType),
  );
  const [date, setDate] = useState(todayIso());
  const [bank, setBank] = useState<string | null>(null);
  const [amount, setAmount] = useState(outstanding);
  const [reference, setReference] = useState('');
  useEffect(() => {
    if (open) {
      setDate(todayIso());
      setAmount(outstanding);
      setReference('');
    }
  }, [open, outstanding]);
  useEffect(() => {
    if (!bank && banks[0]) setBank(banks[0].id);
  }, [bank, banks]);
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/payments`, {
        method: 'POST',
        body: JSON.stringify({ date, bankAccountId: bank, amount, reference: reference.trim() }),
      }),
    onSuccess: async (saved) => {
      await invalidate();
      const p = saved.payments.at(-1);
      toast(
        refund ? t('taxPages.vatReturn.refundRecorded') : t('taxPages.vatReturn.paymentRecorded'),
        {
          ...(p
            ? {
                link: {
                  to: `/accounting/journal-entries/${p.entryId}`,
                  label: t('taxPages.vatReturn.viewJournal'),
                },
              }
            : {}),
        },
      );
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {refund
              ? t('taxPages.vatReturn.recordVatRefund')
              : t('taxPages.vatReturn.recordVatPayment')}
          </DialogTitle>
          <DialogDescription>
            {t(
              refund
                ? 'taxPages.vatReturn.refundDescription'
                : 'taxPages.vatReturn.paymentDescription',
              { amount: ltr(`${r.currency} ${formatAmount(outstanding)}`) },
            )}
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3">
          <Field label={t('taxPages.vatReturn.date')} error={errors['date']}>
            {(id) => <DateInput id={id} value={date} onChange={setDate} />}
          </Field>
          <Field label={t('taxPages.vatReturn.bankAccount')} error={errors['bankAccountId']}>
            {() => (
              <Combobox
                ariaLabel={t('taxPages.vatReturn.bankAccount')}
                items={banks.map((a) => ({ id: a.id, label: a.fullName, keywords: a.code }))}
                value={bank}
                onChange={setBank}
              />
            )}
          </Field>
          <Field
            label={t('taxPages.vatReturn.amountCurrency', { currency: r.currency })}
            error={errors['amount']}
          >
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 rounded-md border border-input bg-card px-3 text-end text-sm tnum"
                value={amount}
                onValueChange={setAmount}
              />
            )}
          </Field>
          <Field
            label={t('taxPages.vatReturn.referenceOptional')}
            hint={t('taxPages.vatReturn.referenceHint')}
          >
            {(id) => (
              <Input
                id={id}
                value={reference}
                maxLength={60}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending || !bank || !amount}>
            {m.isPending ? t('common.saving') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReopenDialog({ open, r, onClose }: { open: boolean; r: VatReturn; onClose: () => void }) {
  const t = useT();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/reopen`, {
        method: 'POST',
        body: JSON.stringify({ reason: reason.trim() }),
      }),
    onSuccess: async () => {
      await invalidate();
      toast(t('taxPages.vatReturn.reopened', { label: ltr(r.label) }));
      onClose();
    },
  });
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('taxPages.vatReturn.reopenTitle', { label: r.label })}</DialogTitle>
          <DialogDescription>{t('taxPages.vatReturn.reopenDescription')}</DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error?.message}</ErrorBanner>
        <Field label={t('taxPages.vatReturn.reason')}>
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => m.mutate()}
            disabled={m.isPending || reason.trim().length < 3}
          >
            {t('taxPages.vatReturn.reopenReturn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdjustmentDialog({
  open,
  r,
  onClose,
}: {
  open: boolean;
  r: VatReturn;
  onClose: () => void;
}) {
  const t = useT();
  const boxLabel = useBoxLabel();
  const orgId = useOrgId();
  const invalidate = useInvalidate();
  const [box, setBox] = useState('2');
  const [direction, setDirection] = useState<'increase' | 'decrease'>('decrease');
  const [amount, setAmount] = useState('');
  const [vat, setVat] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setBox('2');
      setDirection('decrease');
      setAmount('');
      setVat('');
      setReason('');
    }
  }, [open]);
  const amountOnly = box === '4' || box === '5';
  const signed = (v: string) => (v ? (direction === 'decrease' ? `-${v}` : v) : '0');
  const m = useMutation({
    mutationFn: () =>
      api<VatReturn>(`/orgs/${orgId}/vat/returns/${r.key}/adjustments`, {
        method: 'POST',
        body: JSON.stringify({
          box,
          amount: signed(amount),
          vat: amountOnly ? '0' : signed(vat),
          reason: reason.trim(),
        }),
      }),
    onSuccess: async () => {
      await invalidate();
      toast(t('taxPages.vatReturn.adjustmentAdded', { box: ltr(box) }));
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  const label = (b: string) => boxLabel(b, r.boxes.find((x) => x.box === b)?.label ?? '');
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('taxPages.vatReturn.addAdjustment')}</DialogTitle>
          <DialogDescription>{t('taxPages.vatReturn.adjustDescription')}</DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('taxPages.vatReturn.box')}
            className="sm:col-span-2"
            error={errors['box']}
          >
            {(id) => (
              <NativeSelect id={id} value={box} onChange={(e) => setBox(e.target.value)}>
                {ADJUSTABLE_BOXES.map((b) => (
                  <option key={b} value={b}>
                    {b} · {label(b)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label={t('taxPages.vatReturn.direction')} className="sm:col-span-2">
            {(id) => (
              <NativeSelect
                id={id}
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'increase' | 'decrease')}
              >
                <option value="decrease">{t('taxPages.vatReturn.decreaseBox')}</option>
                <option value="increase">{t('taxPages.vatReturn.increaseBox')}</option>
              </NativeSelect>
            )}
          </Field>
          <Field
            label={t('taxPages.vatReturn.amountCurrency', { currency: r.currency })}
            error={errors['amount']}
          >
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 rounded-md border border-input bg-card px-3 text-end text-sm tnum"
                value={amount}
                onValueChange={setAmount}
              />
            )}
          </Field>
          <Field
            label={t('taxPages.vatReturn.vatCurrency', { currency: r.currency })}
            error={errors['vat']}
          >
            {(id) => (
              <AmountInput
                id={id}
                disabled={amountOnly}
                className="h-9 rounded-md border border-input bg-card px-3 text-end text-sm tnum"
                value={amountOnly ? '' : vat}
                onValueChange={setVat}
              />
            )}
          </Field>
          <Field
            label={t('taxPages.vatReturn.reason')}
            className="sm:col-span-2"
            error={errors['reason']}
          >
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => m.mutate()}
            disabled={m.isPending || reason.trim().length < 3 || (!amount && !(vat && !amountOnly))}
          >
            {t('taxPages.vatReturn.addAdjustment')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
